/**
 * Plays a single beep using the Web Audio API.
 * @param frequency Frequency of the beep in Hz (default: 440)
 * @param duration Duration of the beep in ms (default: 200)
 * @param type Oscillator type (default: 'sine')
 */
export const playBeep = (
  frequency = 1000,
  duration = 200,
  type: OscillatorType = 'sine',
) => {
  // webkitAudioContext: prefixo legacy de Safari iOS/Tizen; não está na DOM
  // lib oficial (apenas window.AudioContext). Cast tipado evita `any`.
  const AudioContext =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof window.AudioContext })
      .webkitAudioContext;
  if (!AudioContext) return;

  const audioCtx = new AudioContext();
  const oscillator = audioCtx.createOscillator();
  const gainNode = audioCtx.createGain();

  oscillator.type = type;
  oscillator.frequency.value = frequency;

  oscillator.connect(gainNode);
  gainNode.connect(audioCtx.destination);

  oscillator.start();

  // Fade out
  gainNode.gain.setValueAtTime(1, audioCtx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(
    0.001,
    audioCtx.currentTime + duration / 1000,
  );

  setTimeout(() => {
    oscillator.stop();
    audioCtx.close();
  }, duration);
};

/**
 * Plays a sequence of beeps.
 * Returns a promise that resolves when the sequence (including last beep duration) finishes.
 */
export const playAlertSequence = (count = 3, interval = 400): Promise<void> => {
  return new Promise((resolve) => {
    const beepDuration = 200;

    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        playBeep(1000, beepDuration);

        // If this is the last beep, verify completion
        if (i === count - 1) {
          setTimeout(() => {
            resolve();
          }, beepDuration + 100); // Small buffer
        }
      }, i * interval);
    }
  });
};
